import React, { useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Center, Environment, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';

const SkullPart = ({ part, isSelected, onClick }) => {
    const meshRef = useRef();
    const [hovered, setHover] = useState(false);

    useFrame(() => {
        if (!meshRef.current) return;
        const targetScale = hovered || isSelected ? 1.08 : 1;
        meshRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.15);
    });

    let geometryArgs = part.args;
    
    // Low poly rendering with basic shapes
    return (
        <group position={part.position} rotation={part.rotation || [0, 0, 0]}>
            <mesh
                ref={meshRef}
                onClick={(e) => { e.stopPropagation(); onClick(part.id); }}
                onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
                onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
            >
                {part.shape === 'box' ? (
                    <boxGeometry args={geometryArgs} />
                ) : part.shape === 'sphere' ? (
                    <icosahedronGeometry args={[geometryArgs[0], 1]} />
                ) : (
                    <cylinderGeometry args={geometryArgs} />
                )}
                
                <meshStandardMaterial
                    color={isSelected ? "#ef4444" : (hovered ? "#fca5a5" : "#f8fafc")}
                    emissive={isSelected ? "#b91c1c" : "#0f172a"}
                    emissiveIntensity={isSelected ? 0.3 : 0.02}
                    roughness={0.8}
                    metalness={0.1}
                    flatShading={true}
                />
            </mesh>
            {(hovered || isSelected) && (
                <Text
                    position={[0, part.textY || 20, 15]}
                    fontSize={5}
                    color={isSelected ? "#ef4444" : "#64748b"}
                    anchorX="center"
                    anchorY="middle"
                    fontWeight="bold"
                >
                    {part.label}
                </Text>
            )}
        </group>
    );
};

export const skullPartsData = [
    { id: 'frontal', label: 'Forehead (Frontal)', shape: 'sphere', args: [26], position: [0, 32, 0], textY: 30 },
    { id: 'leftOrbital', label: 'Left Orbital', shape: 'box', args: [14, 12, 10], position: [-14, 12, 18], textY: 15 },
    { id: 'rightOrbital', label: 'Right Orbital', shape: 'box', args: [14, 12, 10], position: [14, 12, 18], textY: 15 },
    { id: 'nasal', label: 'Nose (Nasal)', shape: 'cylinder', args: [3, 8, 16, 4], position: [0, 4, 26], rotation: [Math.PI/7, Math.PI/4, 0], textY: 15 },
    { id: 'leftZygomatic', label: 'Left Cheek', shape: 'sphere', args: [13], position: [-24, 0, 14], textY: 20 },
    { id: 'rightZygomatic', label: 'Right Cheek', shape: 'sphere', args: [13], position: [24, 0, 14], textY: 20 },
    { id: 'maxilla', label: 'Upper Jaw (Maxilla)', shape: 'box', args: [22, 12, 14], position: [0, -8, 22], textY: -15 },
    { id: 'mandible', label: 'Lower Jaw (Mandible)', shape: 'box', args: [28, 14, 20], position: [0, -24, 12], textY: -20 },
];

export default function ThreeDSkullChart({ selectedTraumas, onTraumaToggle, darkMode }) {
    const isTouch = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

    const backgroundStyle = darkMode
        ? 'linear-gradient(to bottom, #1e293b, #0f172a)'
        : 'linear-gradient(to bottom, #f1f5f9, #cbd5e1)';

    return (
        <div style={{ width: '100%', height: 'min(500px, 55vh)', position: 'relative', borderRadius: '16px', overflow: 'hidden', background: backgroundStyle, transition: 'background 0.3s ease' }}>
            <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 10, display: 'flex', gap: '10px' }}>
                <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.8)', borderRadius: '8px', fontSize: '0.85rem', color: '#475569', display: 'flex', alignItems: 'center', fontWeight: 600, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    {isTouch ? '👆 Touch to Select' : '🖱️ Click to Select'}
                </div>
            </div>

            <Canvas camera={{ position: [0, 15, 140], fov: 45 }} shadows dpr={[1, 2]}>
                <ambientLight intensity={0.7} />
                <spotLight position={[40, 80, 50]} angle={0.3} penumbra={1} intensity={0.9} castShadow />
                <pointLight position={[-30, 20, 40]} intensity={0.6} color="#e0f2fe" />

                <OrbitControls
                    enablePan={true}
                    enableZoom={true}
                    minDistance={50}
                    maxDistance={250}
                    minPolarAngle={0}
                    maxPolarAngle={Math.PI / 1.4}
                    makeDefault
                />

                <Center>
                    <group position={[0, 5, 0]}>
                        {skullPartsData.map((part) => (
                            <SkullPart
                                key={part.id}
                                part={part}
                                isSelected={selectedTraumas?.includes(part.id)}
                                onClick={onTraumaToggle}
                            />
                        ))}
                    </group>
                </Center>

                <Environment preset="city" />
                <ContactShadows position={[0, -45, 0]} opacity={0.3} scale={120} blur={2.5} far={50} />
            </Canvas>
        </div>
    );
}
